import { audioSettingsStorage } from "@/utils/audioSettings";
import createDebug from "debug";
import { useCallback, useEffect, useRef, useState } from "react";

const debug = createDebug("cohesive:audio-recording-hook");

// Maximum recording duration in seconds (2 minutes)
const MAX_RECORDING_DURATION = 120;

interface UseAudioRecordingOptions {
  onRecordingComplete?: (audioBlob: Blob) => void;
  onError?: (error: Error) => void;
  onMaxDurationReached?: () => void;
}

interface UseAudioRecordingReturn {
  isRecording: boolean;
  isSupported: boolean;
  audioLevel: number;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  duration: number;
}

export const useAudioRecording = ({
  onRecordingComplete,
  onError,
  onMaxDurationReached,
}: UseAudioRecordingOptions = {}): UseAudioRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Use a ref for recording state to avoid stale closure issues
  const isRecordingRef = useRef<boolean>(false);

  // Web Audio API refs for real-time audio analysis
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Check if audio recording is supported
  const isSupported = !!(
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof window !== "undefined" &&
    window.MediaRecorder &&
    window.AudioContext
  );

  // Real-time audio level analysis using Web Audio API
  const updateAudioLevel = useCallback(() => {
    if (
      !isRecordingRef.current ||
      !analyserRef.current ||
      !dataArrayRef.current
    ) {
      debug("updateAudioLevel: Missing requirements", {
        isRecording: isRecordingRef.current,
        hasAnalyser: !!analyserRef.current,
        hasDataArray: !!dataArrayRef.current,
      });
      return;
    }

    // Get frequency data from the analyser
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Calculate average volume level
    let sum = 0;
    let maxValue = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += dataArrayRef.current[i];
      maxValue = Math.max(maxValue, dataArrayRef.current[i]);
    }
    const average = sum / dataArrayRef.current.length;

    // Log raw data occasionally for debugging
    if (Math.random() < 0.01) {
      debug("Audio data:", {
        dataArrayLength: dataArrayRef.current.length,
        rawAverage: average,
        maxValue,
        sampleValues: Array.from(dataArrayRef.current.slice(0, 10)), // First 10 values
      });
    }

    // Normalize to 0-1 range and apply some smoothing
    const normalizedLevel = average / 255;

    // Apply logarithmic scaling for better visual representation
    const logLevel =
      normalizedLevel > 0 ? Math.log10(normalizedLevel * 9 + 1) : 0;

    // Ensure minimum level for visual feedback and cap at 1.0 (reduced sensitivity)
    const level = Math.max(0.05, Math.min(1.0, logLevel * 0.8));

    // Log processed level occasionally
    if (Math.random() < 0.005) {
      debug("Audio levels:", {
        normalizedLevel,
        logLevel,
        finalLevel: level,
      });
    }

    setAudioLevel(level);

    if (isRecordingRef.current) {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, []); // Remove isRecording from dependencies since we're using the ref

  // Cleanup function
  const cleanup = useCallback(() => {
    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clear duration timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Clean up Web Audio API resources
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      // Close audio context to free resources
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(debug);
      }
      audioContextRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Reset refs
    mediaRecorderRef.current = null;
    startTimeRef.current = null;
    dataArrayRef.current = null;
    isRecordingRef.current = false;

    // Reset state
    setAudioLevel(0);
    setDuration(0);
  }, []);

  // Internal stop function to avoid circular dependency
  const internalStopRecording = useCallback(() => {
    debug("🔴 internalStopRecording called", {
      hasMediaRecorder: !!mediaRecorderRef.current,
      isRecording: isRecordingRef.current,
      mediaRecorderState: mediaRecorderRef.current?.state,
    });

    if (mediaRecorderRef.current && isRecordingRef.current) {
      debug("✅ Stopping MediaRecorder...");
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false;
      setIsRecording(false);
      cleanup();
      debug("✅ Recording stopped and cleaned up");
    } else {
      debug(
        "❌ Cannot stop recording - missing MediaRecorder or not recording",
      );
    }
  }, [cleanup]);

  // Start recording function
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      const error = new Error(
        "Audio recording is not supported in this browser",
      );
      setError(error.message);
      onError?.(error);
      return;
    }

    try {
      setError(null);

      // Get user media with the saved device preference and settings
      const savedDevice = audioSettingsStorage.getSelectedDevice();
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100,
      };

      // Use the saved device if available
      if (savedDevice?.deviceId) {
        audioConstraints.deviceId = { exact: savedDevice.deviceId };
        debug("Using saved audio device:", savedDevice.label);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      streamRef.current = stream;

      debug("Audio recording started successfully");

      // Set up Web Audio API for real-time audio analysis
      debug("Setting up Web Audio API...");
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      debug("AudioContext created:", {
        sampleRate: audioContext.sampleRate,
        state: audioContext.state,
      });

      // Create analyser node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // This gives us 128 frequency bins
      analyser.smoothingTimeConstant = 0.8; // Smooth the audio data
      analyserRef.current = analyser;
      debug("Analyser created:", {
        fftSize: analyser.fftSize,
        frequencyBinCount: analyser.frequencyBinCount,
        smoothingTimeConstant: analyser.smoothingTimeConstant,
      });

      // Create data array for frequency data
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(new ArrayBuffer(bufferLength));
      dataArrayRef.current = dataArray;
      debug("Data array created with length:", bufferLength);

      // Create source node from the stream and connect to analyser
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceNodeRef.current = source;
      debug("MediaStreamSource connected to analyser");

      // Resume audio context if it's suspended (required by some browsers)
      if (audioContext.state === "suspended") {
        debug("AudioContext is suspended, resuming...");
        await audioContext.resume();
        debug("AudioContext resumed, state:", audioContext.state);
      }

      // Set up MediaRecorder - keep this EXACTLY as it was working
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        debug("🎵 MediaRecorder.onstop triggered", {
          chunksLength: chunksRef.current.length,
          mimeType: mediaRecorder.mimeType,
        });
        const audioBlob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType,
        });
        debug("🎵 Audio blob created", {
          size: audioBlob.size,
          type: audioBlob.type,
        });
        onRecordingComplete?.(audioBlob);
        chunksRef.current = [];
        debug("🎵 onRecordingComplete called");
      };

      mediaRecorderRef.current = mediaRecorder;

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms

      // Set recording state BEFORE starting audio analysis
      isRecordingRef.current = true;
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        if (startTimeRef.current && isRecordingRef.current) {
          const currentDuration = Math.floor(
            (Date.now() - startTimeRef.current) / 1000,
          );

          // Cap the duration at MAX_RECORDING_DURATION to prevent negative numbers
          const cappedDuration = Math.min(
            currentDuration,
            MAX_RECORDING_DURATION,
          );
          setDuration(cappedDuration);

          // Check if max duration has been reached
          if (currentDuration >= MAX_RECORDING_DURATION) {
            debug("🛑 Max duration reached! Stopping recording...", {
              currentDuration,
              MAX_RECORDING_DURATION,
              isRecordingRef: isRecordingRef.current,
              hasMediaRecorder: !!mediaRecorderRef.current,
            });
            onMaxDurationReached?.();
            internalStopRecording();
            return; // Exit the interval callback early
          }
        }
      }, 1000);

      // Start real-time audio level analysis
      debug("Starting real-time audio level analysis...");
      updateAudioLevel();
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to start recording");
      setError(error.message);
      onError?.(error);
      cleanup();
    }
  }, [
    isSupported,
    onRecordingComplete,
    onError,
    onMaxDurationReached,
    updateAudioLevel,
    internalStopRecording,
  ]);

  // Stop recording function (public API)
  const stopRecording = useCallback(() => {
    internalStopRecording();
  }, [internalStopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    isRecording,
    isSupported,
    audioLevel,
    error,
    startRecording,
    stopRecording,
    duration,
  };
};
